// The top-level (parent) route
/* eslint-disable global-require */

const routes = {
  path: '',

  // Keep in mind, routes are evaluated in order
  children: [
    {
      path: '',
      load: () => import(/* webpackChunkName: 'home' */ './home'),
    },
    {
      path: '/portal',
      children: [
        {
          path: '/:portalId',
          load: () => import(/* webpackChunkName: 'home' */ './home'),
        },
      ],
    },
    {
      path: '/workspaces',
      load: () =>
        import(/* webpackChunkName: 'onboarding-users' */ './onboarding/users'),
    },
    {
      path: '/clients',
      children: [
        {
          path: '/users',
          children: [
            {
              path: '',
              load: () => import(/* webpackChunkName: 'users' */ './users'),
            },
            {
              path: '/details',
              load: () =>
                import(
                  /* webpackChunkName: 'client-details-page' */ './users/ClientsDetailsPage'
                ),
            },
          ],
        },
        {
          path: '/import',
          load: () =>
            import(
              /* webpackChunkName: 'client-imports-page' */ './users/ClientImportPage'
            ),
        },
      ],
    },
    {
      path: '/invoices',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'invoices' */ './invoices'),
        },
        {
          path: '/pay',
          load: () =>
            import(/* webpackChunkName: 'invoices-pay' */ './invoices/pay'),
        },
        {
          path: '/edit',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'invoice-edit' */ './invoices/edit'
                ),
            },
            {
              path: '/template',
              load: () =>
                import(
                  /* webpackChunkName: 'invoice-template' */ './invoices/template'
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/subscriptions',
      children: [
        {
          path: '',
          load: () =>
            import(/* webpackChunkName: 'subscriptions' */ './subscriptions'),
        },
        {
          path: '/edit',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'subscription-edit' */ './subscriptions/edit'
                ),
            },
            {
              path: '/template',
              load: () =>
                import(
                  /* webpackChunkName: 'subscription-template' */ './subscriptions/template'
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/login',
      load: () => import(/* webpackChunkName: 'login' */ './auth'),
    },
    {
      path: '/browser-unsupported',
      load: () =>
        import(
          /* webpackChunkName: 'browser-unsupported' */ './browser-unsupported'
        ),
    },
    // Following routes serve as redirection to helpdesk route as knowledge base it self has a path change
    {
      path: '/knowledge-base',
      children: [
        {
          path: '',
          action() {
            return { redirect: '/helpdesk' };
          },
        },
        {
          path: '/article/:id',
          action({ params }: { params: { id: string } }) {
            return { redirect: `/helpdesk/article/${params.id}` };
          },
        },
      ],
    },
    {
      path: '/helpdesk',
      children: [
        {
          path: '',
          load: () =>
            import(/* webpackChunkName: 'knowledge-base' */ './knowledgebase'),
        },
        {
          path: '/article/:id',
          load: () => import(/* webpackChunkName: 'article' */ './article'),
        },
      ],
    },
    {
      path: '/files',
      load: () => import(/* webpackChunkName: 'files' */ './files'),
    },
    {
      path: '/notifications',
      load: () => import(/* webpackChunkName: 'inbox' */ './inbox'),
    },
    {
      path: '/extensions',
      load: () => import(/* webpackChunkName: 'extensions' */ './extensions'),
    },
    {
      path: '/messaging',
      load: () => import(/* webpackChunkName: 'messaging' */ './messaging'),
    },
    {
      path: '/marketingsite',
      load: () =>
        import(/* webpackChunkName: 'marketingsite' */ './marketingsite'),
    },
    {
      path: '/signup',
      load: () =>
        import(/* webpackChunkName: 'onboarding-setup' */ './onboarding/setup'),
    },
    {
      path: '/admin',
      load: () =>
        import(/* webpackChunkName: 'onboarding-login' */ './onboarding/login'),
    },
    {
      path: '/add-account',
      load: () =>
        import(/* webpackChunkName: 'onboarding-login' */ './onboarding/login'),
    },
    {
      path: '/signature',
      load: () =>
        import(/* webpackChunkName: 'signature' */ './signature/index.v2'),
    },
    {
      path: '/signature-v2',
      load: () =>
        import(/* webpackChunkName: 'signature-v2' */ './signature/index.v2'),
    },
    {
      path: '/forms',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'forms' */ './forms'),
        },
        {
          path: '/edit',
          load: () =>
            import(/* webpackChunkName: 'form-edit' */ './forms/edit'),
        },
        {
          path: '/response',
          load: () =>
            import(/* webpackChunkName: 'form-response' */ './forms/response'),
        },
        {
          path: '/details',
          load: () =>
            import(
              /* webpackChunkName: 'form-v2-details' */ './forms-v2/form-details'
            ),
        },
        {
          path: '/submit',
          load: () =>
            import(/* webpackChunkName: 'form-submit' */ './forms/submit'),
        },
      ],
    },
    // Customization and modules being a standalone route instead of child of settings.
    // The original route are still available for backward compatibility.
    {
      path: '/customization',
      load: () =>
        import(
          /* webpackChunkName: 'settings-customization' */ './settings/customization'
        ),
    },
    {
      path: '/module-management',
      children: [
        {
          path: '',
          load: () =>
            import(
              /* webpackChunkName: 'settings-module-management' */ './settings/module-management'
            ),
        },
        {
          path: '/new',
          load: () =>
            import(
              /* webpackChunkName: 'settings-module-management-new-module' */ './settings/module-management/new-module'
            ),
        },
        {
          path: '/setup',
          load: () =>
            import(
              /* webpackChunkName: 'settings-module-management-setup-module' */ './settings/module-management/setup-module'
            ),
        },
      ],
    },
    {
      path: '/referrals',
      load: () => import(/* webpackChunkName: 'referrals' */ './referrals'),
    },
    {
      path: '/settings',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'settings' */ './settings'),
        },
        {
          path: '/me',
          load: () =>
            import(
              /* webpackChunkName: 'settings-account' */ './settings/account'
            ),
        },
        {
          path: '/main',
          load: () =>
            import(
              /* webpackChunkName: 'settings-main' */ './settings/general'
            ),
        },

        {
          path: '/domain',
          load: () =>
            import(
              /* webpackChunkName: 'settings-domain' */ './settings/domain'
            ),
        },
        {
          path: '/members',
          load: () =>
            import(/* webpackChunkName: 'settings-team' */ './settings/team'),
        },
        {
          path: '/notifications',
          load: () =>
            import(
              /* webpackChunkName: 'settings-notifications' */ './settings/notifications'
            ),
        },
        {
          path: '/api',
          children: [
            {
              path: '',
              load: () =>
                import(/* webpackChunkName: 'settings-api' */ './settings/api'),
            },
            {
              path: '/webhooks',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-api-webhooks' */ './settings/api/webhooks'
                ),
            },
            {
              path: '/setup',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-module-management-setup-module' */ './settings/module-management/setup-module'
                ),
            },
          ],
        },
        {
          path: '/apps',
          load: () =>
            import(/* webpackChunkName: 'settings-apps' */ './settings/apps'),
        },

        {
          path: '/plan',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans' */ './settings/plans'
                ),
            },
            {
              path: '/cancel',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans-cancel-plan-start' */ './settings/plans/cancel-plan-start'
                ),
            },
            {
              path: '/cancel/confirm',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans-cancel-plan-confirm' */ './settings/plans/cancel-plan-confirm'
                ),
            },
          ],
        },
        {
          path: '/referral',
          load: () =>
            import(
              /* webpackChunkName: 'settings-referral' */ './settings/referral'
            ),
        },
        {
          path: '/messaging',
          load: () =>
            import(
              /* webpackChunkName: 'settings-messaging' */ './settings/messaging'
            ),
        },
        {
          path: '/files',
          load: () =>
            import(/* webpackChunkName: 'settings-files' */ './settings/files'),
        },
        {
          path: '/payments',
          load: () =>
            import(
              /* webpackChunkName: 'settings-payments' */ './settings/payments'
            ),
        },
        {
          path: '/billing',
          load: () =>
            import(
              /* webpackChunkName: 'settings-billing' */ './settings/billing'
            ),
        },
      ],
    },
    // Wildcard routes, e.g. { path: '(.*)', ... } (must go last)
    {
      path: '(.*)',
      load: () => import(/* webpackChunkName: 'not-found' */ './not-found'),
    },
  ],

  async action({ next }) {
    // Execute each child route until one of them return the result
    const route = await next();

    // Provide default values for title, description etc.
    route.title = `${route.title || 'Untitled Page'}`;
    route.description = route.description || '';

    return route;
  },
};

// The error page is available by permanent url for development mode
if (__DEV__) {
  routes.children.unshift({
    path: '/error',
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    action: require('./error').default,
  });
}

export default routes;
